import Analytics from '@/mixins/Analytics';
import importFederation from '@/utilities/mfe/importFederation';

// Fetch imports in parallel
const [
  capitalFeatureFlagsSlugs,
  inventoryFeatureFlagsSlugs,
  pandaFeatureFlagsSlugs,
  offersPortalFeatureFlagsSlugs,
] = await Promise.all([
  /** Provide FF slugs from Capital remote app */
  importFederation('capital/featureFlagSlugs')
    .then((data) => data.default)
    .catch((e) => {
      Analytics.methods.noticeError('CAPITAL FEATURE FLAG SLUGS FAILED', e);

      return {};
    }),

  /** Provide FF slugs from Capital remote app */
  importFederation('inventory/featureFlagSlugs')
    .then((data) => data.default)
    .catch((e) => {
      Analytics.methods.noticeError('My Inventory MFE feature flags failed', e);

      return {};
    }),

  /** Provide FF slugs from Panda remote app */
  importFederation('panda/featureFlagSlugs')
    .then((data) => data.default)
    .catch((e) => {
      Analytics.methods.noticeError('Panda feature flags failed', e);

      return {};
    }),

  /** Provide FF slugs from Offers Portal remote app */
  importFederation('offersPortal/featureFlagSlugs')
    .then((data) => data.default)
    .catch((e) => {
      Analytics.methods.noticeError('Offers Portal feature flags failed', e);

      return {};
    }),
]);

/* eslint sort-keys: "error" */ /* nop */
const featureFlagSlugs = {
  ...capitalFeatureFlagsSlugs,
  ...inventoryFeatureFlagsSlugs,
  ...offersPortalFeatureFlagsSlugs,
  ...pandaFeatureFlagsSlugs,
  acvOfferMarketReportGuarantee: 'acv_offer_market_report_guarantee',
  acvProgrammaticNotifications: 'acv_programmatic_notifications',
  acvSam3pPrivateRules: 'sam_3p_private_rules',
  addRestrictionsToSellerControlledStartPrice: 'add_restrictions_to_seller_controlled_start_price',
  allowSellerControlledStartPriceOnLaunch: 'allow_seller_controlled_start_price_on_launch',
  arbitrationIntakeDesiredOutcome: 'arbitration_intake_desired_outcome',
  arbitrationIntakeViewSubmittedArb: 'arbitration_intake_view_submitted_arb',
  arbitrationWebIntakeV2: 'arb_webintake_v2',
  auctionProductsSellerNameDisplay: 'auction_products_seller_name_display',
  auctionProductsShowAcvMaxRetailPrice: 'auction_products_show_acv_max_retail_price',
  auctionSellerCanKillAuction: 'auction_seller_can_kill_auction',
  auctionSrpRefreshHideLanes: 'auctions_srp_refresh_hide_lanes',
  auctionsPlatformMinimumProxyBidValidation: 'auctions_platform_minimum_proxy_bid_validation',
  auctionsProductShowBuyersNotes: 'auctions_product_show_buyers_notes',
  bidUseAuctionHouseEndpointWeb: 'bid_use_auction_house_endpoint_web',
  biddingButtonContentUpdate: 'bidding_button_content_update',
  buyingMatrixLink: 'buying_matrix_link',
  capitalABTestingLeadGenWidgetCheckout: 'capital_abtesting_leadgenwidget_checkout',
  capitalABTestingLeadGenWidgetCheckoutB: 'capital_abtesting_leadgenwidget_checkout_b',
  capitalCheckAmountOutOfRange: 'capital_check_amount_out_of_range',
  capitalCheckoutPromotions: 'capital_checkout_promotions',
  capitalFloatToFloorV2: 'capital_float_to_floor_v2',
  capitalFlooringLandingPageUpdate: 'capital_flooring_landing_page_update',
  capitalInvoiceRefactor: 'capital_invoice_refactor',
  capitalNewPromoProductOffering: 'capital_new_promo_product_offering',
  capitalNewUtilizationScenariosOffering: 'capital_new_utilization_scenarios_offering',
  capitalPilotAdvantage: 'capital_pilot_advantage',
  capitalShowAdvantageLeadGen: 'capital_show_advantage_lead_gen',
  capitalShowPortfolioSummary: 'capital_show_portfolio_summary',
  capitalTitleFlexProduct: 'capital_title_flex_product',
  capitalUseContractAcvAdvantage: 'capital_use_contract_acv_advantage',
  capitalUseMicrofrontend: 'capital_use_microfrontend',
  capitalUseWebComponent: 'capital_use_web_component',
  conditionalFieldsForMultifunctionalityLaunch: 'conditional_fields_for_multifunctionality_launch',
  crExpiration: 'cr_expiration',
  crExpirationExempt: 'cr_expiration_exempt',
  datadogSessionReplay: 'datadog_session_replay_web',
  dealerDocsUploadDisabled: 'disable_user_dealer_docs_upload',
  disableSRPDisplayRunList: 'disable_search_results_page_display_run_list',
  displaySrpViewBidCountWeb: 'display_srp_view_bid_count_web',
  enableAcvLoyaltyMultiDealer: 'marketplace_enable_acv_loyalty_for_multi_dealer',
  enableAcvLoyaltyProduct: 'enable_acv_loyalty_product',
  enableAcvLoyaltyProgressCard: 'marketplace_enable_show_loyalty_progress',
  enableClockDriftTracking: 'enable_clock_drift_event_tracking',
  enableConditionInclusions: 'ser_enable_condition_inclusions',
  enableDevtoolsWeb: 'enable_devtools_web',
  enableEngineCylindersFilter: 'sn_filter_engine_cylinders',
  enableGraphqlOnSrp: 'enable_graphql_on_srp',
  enableMandatedTransport: 'enable_mandated_transport',
  enableNewFeeSchedulePage: 'enable_new_fee_schedule_page',
  enableOmsItemizedBuyFees: 'enable_oms_itemized_buy_fees',
  enableRescheduleAuctions: 'enable_reschedule_auctions',
  enableSellerControlRoomWeb: 'enable_seller_control_room_web',
  enableSingleLaunchBiddingEngagementMeter: 'enable_single_launch_bidding_engagement_meter',
  enableStockwaveIntegration: 'enable_stockwave_integration',
  enableTosAcceptance: 'enable_terms_of_service_acceptance',
  enableTrackPendoInsteadOfMixpanel: 'enable_track_pendo_instead_of_mixpanel',
  enableTransportPromoOnSRP: 'enable_transport_promo_on_srp',
  enhancedPhotoGallery: 'enhanced_photo_gallery',
  forceAuctionsFinalize: 'force_auctions_finalize',
  gsalePerformanceDashboardActivityColumn: 'gsale_performance_dashboard_activity_column',
  hideMarketReportMinMaxBoxes: 'market_report_hide_min_max',
  inboundShippingLabelConsolidation: 'inbound_shipping_label_consolidation',
  launchDefaultReserveInputsToReserveValue: 'launch_default_reserve_inputs_to_reserve_value',
  launchDisableScheduledAuctionOnReruns: 'launch_disable_scheduled_auction_on_reruns',
  launchEnableStartPriceBasedOnReservePrice: 'launch_enable_start_price_based_on_reserve_price',
  launchExperimentalLaunchFlowRestriction: 'launch_experimental_launch_flow_restriction',
  launchNewAuctionPricingContract: 'launch_new_auction_pricing_contract',
  launchUseBulkAuctionLaunchForSingleLaunch: 'launch_use_bulk_auction_launch_for_single_launch',
  leadSendNotificationSuppression: 'lead_send_notification_suppression',
  liftListedAsReplaceDueDate: 'lift_listedas_replace_duedate',
  marketReportShowEngineDescriptionFilter: 'market_report_show_engine_description_filter',
  marketReportV2: 'market_report_v2',
  matchForecastUpdates: 'match_forecast_updates',
  myAcvDisplayedItemTracking: 'my_acv_displayed_item_tracking',
  myAcvEnableBuyerTitle: 'my_acv_enable_buyer_title',
  myAcvOmniPerformance: 'myacv_omni_performance',
  myAcvOmniPerformanceBuyer: 'myacv_omni_performance_buyer',
  myAcvOmniPerformanceFrontendRework: 'myacv_omni_performance_frontend_rework',
  myAcvShowCobrandingSection: 'show_cobranding_section',
  myAcvShowInvoicesMenu: 'show_my_acv_invoice_menu',
  myAcvShowSalesPerformanceInformation: 'myacv_show_sales_performance_information',
  myAcvShowStandardContactInfo: 'myacv_show_standard_contact_info',
  myAcvShowTitleProgressDetails: 'myacv_show_title_progress_details',
  myInventoryAddGuaranteedSaleOffers: 'my_inventory_add_guaranteed_sale_offers',
  myInventoryAddNoReserveSchedules: 'my_inventory_add_no_reserve_schedules',
  myInventoryCms: 'my_inventory_cms',
  myInventoryDefaultLaunchValues: 'my_inventory_default_launch_values',
  myInventoryEnabled: 'my_acv_my_inventory',
  myInventoryMigrateVdpSaDelete: 'my_inventory_migrate_vdp_sa_delete',
  nearReserveBadgeSeenTracking: 'near_reserve_badge_seen_tracking',
  noReservePreviewLaunchWindowsUseGse: 'no_reserve_preview_launch_windows_use_gse',
  oNMultiDealerFilter: 'o_n_multi_dealer_filter',
  oNOffersDashboardImprovements: 'o_n_offers_dashboard_improvements',
  oNOffersDashboardUiTweaks: 'o_n_offers_dashboard_ui_tweaks',
  offersAndNegotiationsPersistentProxyPrompt: 'offers_and_negotiations_persistent_proxy_prompt',
  offersAndNegotiationsProxyBidCounter: 'offers_and_negotiations_proxy_bid_counter',
  offersAndNegotiationsRestrictOfferHistory: 'offers_and_negotiations_restrict_offer_history',
  offersAndNegotiationsShowOfferHistory: 'offers_and_negotiations_show_offer_history',
  offersAndNegotiationsSplitDifference: 'offers_and_negotiations_split_difference',
  offersPageAddGuaranteedSaleOffers: 'offers_page_add_guaranteed_sale_offers',
  onForcedOfferRejection: 'o_n_forced_offer_rejection',
  onUseLiveAppraisalDealType: 'o_n_use_live_appraisal_deal_type',
  otherReportAProblemOptionOpensChatWidget: 'other_report_a_problem_option_opens_chat_widget',
  passwordResetRequest: 'password_reset_request',
  paymentChargeFloorplanFacilitationFee: 'payment_charge_floorplan_facilitation_fee',
  photoViewedTracking: 'photo_viewed_tracking',
  privateAuctionsUseMinOfferAmount: 'private_auctions_use_min_offer_amount',
  proxyBidInfoUseAuctionHouseEndpointWeb: 'web_ah_handles_proxy_bid_info',
  proxyBidTypesUseAuctionHouseEndpointWeb: 'web_ah_handles_proxy_bid_types',
  qeMigrateRecentToSS: 'qe_migrate_recent_to_ss',
  requestOffPlatformTransportation: 'request_off_platform_transportation',
  requireEulaAcceptanceForLanes: 'require_eula_acceptance_for_lanes',
  reserveMetBadgeSeenTracking: 'reserve_met_badge_seen_tracking',
  restrictManageUserToPrimaryContact: 'restrict_manage_users_to_primary_contact',
  routeCountsV3ToSearchServiceDirectly: 'route_counts_v3_to_search_service_directly',
  savedAuctionsAddGuaranteedOffer: 'saved_auction_add_guaranteed_sale_offers',
  savedSearchConditionExclusions: 'saved_search_condition_exclusions',
  savedSearchConditionExclusionsDealerships: 'saved_search_condition_exclusions_dealerships',
  savedSearchTemplates: 'saved_search_templates',
  searchShowAuctionTypeFilterWeb: 'search_show_auction_type_filter_web',
  searchWebAuctionRecommendationBadging: 'search_web_auction_recommendation_badging',
  selectAllMakesModelsWeb: 'ser_select_all_makes_models_web',
  setSavedAuctionContext: 'market_report_set_saved_auction_context',
  showAddClerkTour: 'show_add_clerk_acv_tour',
  showAddClerkTourABTest: 'show_add_clerk_acv_tour_a_b_test',
  showBuyerStateTitleRequirements: 'show_buyer_state_title_requirements',
  showCheckoutTransportationMessage: 'show_checkout_transportation_message',
  showClearCarAppraisalTab: 'acv_header_drivably_appraisal_tab',
  showDigitalRunSheet: 'digital_run_sheet_web',
  showDigitalRunSheetAcvCalculatedValue: 'digital_run_sheet_show_sweet_spot',
  showDigitalRunSheetFeedbackWidget: 'digital_run_sheet_feedback_widget',
  showEstimateMakeOffer: 'show_estimate_make_offer',
  showEstimateOfferDashboard: 'show_estimate_offer_dashboard',
  showFinalOfferButton: 'show_final_offer_button',
  showFloorPriceInMarketReport: 'market_report_floor_price_visible',
  showInvoicesToBillingContact: 'show_invoices_to_billing_contact',
  showMarketReportFewResults: 'market_report_show_few_results',
  showMarketReportInAuctionLaunchIOS: 'market_comp_link_ios',
  showMarketReportTabIOS: 'market_comp_tab_ios',
  showMyAcvAcquisitionsPage: 'show_myacv_acquisitions_page',
  showOfferRejectionOptions: 'show_offer_rejection_options',
  showOutstandingTitleDetails: 'show_outstanding_title_details',
  showPaymentMethods: 'assembly_payments_in_app',
  showPaymentSettings: 'vue_payment_settings',
  showPreSaleTitles: 'show_pre_sale_titles',
  showPriceRangeInMarketReportBadge: 'show_price_range_in_market_report_badge',
  showPricingInspectionInfo: 'show_pricing_inspection_info',
  showReportIssue: 'show_report_issue',
  showSavedAuctionLaunchGoGreenDisclaimer: 'saved_auction_launch_go_green_disclaimer',
  showSellerDetailsInVdp: 'show_seller_details_in_vdp',
  showSellerLastEngagedDate: 'show_seller_last_engaged_date',
  showSellerUpdateTemplate: 'show_seller_update_template',
  showSimulcastEventsDirectory: 'show_simulcast_events_directory',
  showSrpEventDirectory: 'show_srp_event_directory',
  showVectorImageSearchWeb: 'show_vector_image_search_web',
  titlesLimitLabelGeneration: 'titles_limit_label_generation',
  transportCheckoutMultipleDeliveryAddress: 'transport_checkout_multiple_delivery_address',
  transportNextDayNameABTest: 'transport_next_day_name_ab_test',
  transportOffPlatformAddressSelection: 'transport_offplatform_address_selection',
  transportOffPlatformManageGatepass: 'transport_offplatform_manage_gatepass',
  transportOsQuoteInApp: 'transport_os_quote_in_app',
  transportReleaseExport: 'transport_release_export',
  transportationAfterAuctionInApp: 'transportation_after_auction_in_app',
  transportationDriveaway: 'transportation_driveaway',
  transportationEnclosed: 'transportation_enclosed',
  transportationHubAddressName: 'transportation_hub_address_name',
  transportationHubBarcodeScanner: 'transportation_hub_barcode_scanner',
  transportationHubMultiVehicleBulk: 'transportation_hub_multi_vehicle_bulk',
  transportationHubMultiVehicleOrder: 'transportation_hub_multi_vehicle_order',
  transportationHubReleaseSlipFileUpload: 'transportation_hub_release_slip_file_upload',
  transportationRedirectCreateBulkRequestToTyk: 'transport_create_bulk_request_redirect_to_tyk',
  transportationRedirectGatePassToTyk: 'transport_gate_pass_redirect_to_tyk',
  transportationRedirectGetQuoteToTyk: 'transport_get_quote_redirect_to_tyk',
  transportationRedirectVinDecodeToTyk: 'transport_vin_decode_redirect_to_tyk',
  transportationSearchRequestRedirectToTyk: 'transport_search_request_redirect_to_tyk',
  transportationStatusLink: 'transportation_status_link',
  transportationToggleOpenPricingVDP: 'transportation_toggle_open_pricing_vdp',
  transportationUseMicrofrontend: 'transportation_hub_use_microfrontend',
  useBffEndpointForNegotiationBuying: 'use_bff_endpoint_for_negotiation_buying',
  useBffEndpointForNegotiationSelling: 'use_bff_endpoint_for_negotiation_selling',
  useMyAcvWonPageRework: 'use_myacv_won_page_rework',
  useMyAcvWonStatusChips: 'use_myacv_won_status_chips',
  useOpenMarketplaceOffers: 'use_open_marketplace_offers',
  usePandaMfe: 'use_panda_mfe',
  usePandaMfeForRc: 'use_panda_mfe_for_rc',
  vipAddGuaranteeOfferSavedAuctionsLaunch: 'vip_add_guarantee_offer_saved_auctions_launch',
  vipGsaleSavedCardTestv1: 'vip_gsale_saved_card_testv1',
  webMyAcvActiveBuyingUseBff: 'web_myacv_active_buying_use_bff',
};

export default featureFlagSlugs;
